// POS → Restaurant → Reservas (/pos/rest-reservas).
// Agenda de reservas del día por local: crear/editar, marcar LLEGÓ / NO-SHOW /
// cancelar. El plano del Salón pinta la mesa morada con la próxima reserva
// activa de HOY (la reserva se libera al marcarla llegó/no-show/cancelada).
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, Plus, UserX, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { SelectorLocal, Campo, fieldCls, posRpc } from './shared';
import { fechaLocal } from './shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { ReservaT, RestEstadoT, restEstadoQuery, useLocalRestaurant } from './restaurante';
import { LOCALE } from '@/lib/utils/regional';

const ESTADO_BADGE: Record<string, string> = {
  activa: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  llego: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  no_show: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  cancelada: 'bg-muted text-muted-foreground',
};

const FORM0 = { mesa_id: '', hora: '19:00', cliente_nombre: '', telefono: '', personas: '2', notas: '' };

export function ReservasRestaurant() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { locationId, setLocationId, locations } = useLocalRestaurant();
  const [fecha, setFecha] = useState(fechaLocal());
  const [form, setForm] = useState<typeof FORM0 | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  const { data: estado } = useQuery<RestEstadoT>(restEstadoQuery(locationId));
  const mesas = useMemo(() => (estado?.mesas ?? []).slice().sort((a, b) => a.numero - b.numero), [estado]);

  const { data: reservas = [] } = useQuery<ReservaT[]>({
    queryKey: ['rest-reservas', locationId, fecha],
    queryFn: () => posRpc<ReservaT[]>('rest_reservas_listar', { _location: locationId, _fecha: fecha }),
    enabled: !!locationId,
    staleTime: 0, refetchOnMount: 'always', refetchInterval: 30000,
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['rest-reservas'] });
    void qc.invalidateQueries({ queryKey: ['rest-estado'] });
  };
  const cambiarEstado = async (id: string, estadoNuevo: string) => {
    try {
      await posRpc('rest_reserva_estado', { _id: id, _estado: estadoNuevo });
      invalidar();
      toast.success(`Reserva: ${estadoNuevo.replace('_', ' ')}`);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const guardar = async () => {
    if (!form) return;
    try {
      await posRpc('rest_reserva_guardar', {
        _data: {
          mesa_id: form.mesa_id, fecha_hora: `${fecha}T${form.hora}:00`,
          cliente_nombre: form.cliente_nombre, telefono: form.telefono,
          personas: Number(form.personas) || 2, notas: form.notas,
        },
        _id: editId,
      });
      invalidar();
      toast.success(editId ? 'Reserva actualizada' : 'Reserva creada');
      setForm(null); setEditId(null);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      {dlg.ui}
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><CalendarClock className="w-5 h-5" /> Reservas</h1>
        <SelectorLocal locationId={locationId} setLocationId={setLocationId} locations={locations} />
        <input className={`${fieldCls} !w-auto`} type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        <Button size="sm" className="ml-auto" onClick={() => { setEditId(null); setForm({ ...FORM0 }); }}>
          <Plus className="w-4 h-4 mr-1" /> Nueva reserva
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Agenda del {fecha}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b">
                <th className="py-2 pr-3 text-right w-10">N°</th>
                <th className="py-2 pr-3 text-left">Hora</th>
                <th className="py-2 pr-3 text-left">Mesa</th>
                <th className="py-2 pr-3 text-left">Cliente</th>
                <th className="py-2 pr-3 text-right">Personas</th>
                <th className="py-2 pr-3 text-left">Notas</th>
                <th className="py-2 pr-3 text-center">Estado</th>
                <th className="py-2 pr-3" />
              </tr>
            </thead>
            <tbody>
              {reservas.map((r, i) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono">{new Date(r.fecha_hora).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit', hour12: false })}</td>
                  <td className="py-2 pr-3">Mesa {r.mesa_numero}{r.area ? ` (${r.area})` : ''}</td>
                  <td className="py-2 pr-3">
                    <div className="font-medium">{r.cliente_nombre}</div>
                    {r.telefono && <div className="text-xs text-muted-foreground">{r.telefono}</div>}
                  </td>
                  <td className="py-2 pr-3 text-right">{r.personas}</td>
                  <td className="py-2 pr-3 text-xs">{r.notas ?? '—'}</td>
                  <td className="py-2 pr-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${ESTADO_BADGE[r.estado] ?? ''}`}>
                      {r.estado.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">
                    {r.estado === 'activa' && (
                      <>
                        <Button size="sm" variant="outline" title="El cliente llegó"
                                onClick={() => void cambiarEstado(r.id, 'llego')}>
                          <Check className="w-4 h-4 mr-1" /> Llegó
                        </Button>
                        <Button size="sm" variant="ghost" title="No se presentó"
                                onClick={() => void cambiarEstado(r.id, 'no_show')}>
                          <UserX className="w-4 h-4 text-amber-600" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Cancelar reserva"
                                onClick={async () => { if (await dlg.confirmar('¿Cancelar la reserva?', { peligro: true })) void cambiarEstado(r.id, 'cancelada'); }}>
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Editar" onClick={() => {
                          setEditId(r.id);
                          setForm({
                            mesa_id: r.mesa_id,
                            hora: new Date(r.fecha_hora).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit', hour12: false }),
                            cliente_nombre: r.cliente_nombre, telefono: r.telefono ?? '',
                            personas: String(r.personas), notas: r.notas ?? '',
                          });
                        }}>✎</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!reservas.length && (
                <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Sin reservas para esta fecha.</td></tr>
              )}
            </tbody>
          </table>

          {form && (
            <div className="border rounded-md p-3 space-y-3">
              <div className="text-sm font-medium">{editId ? 'Editar reserva' : 'Nueva reserva'}</div>
              <div className="grid gap-3 md:grid-cols-3">
                <Campo label="Mesa *">
                  <select className={fieldCls} value={form.mesa_id} onChange={(e) => setForm({ ...form, mesa_id: e.target.value })}>
                    <option value="">— Selecciona —</option>
                    {mesas.map((m) => <option key={m.id} value={m.id}>Mesa {m.numero} (cap. {m.capacidad})</option>)}
                  </select>
                </Campo>
                <Campo label="Hora *">
                  <input className={fieldCls} type="time" value={form.hora} onChange={(e) => setForm({ ...form, hora: e.target.value })} />
                </Campo>
                <Campo label="Personas">
                  <input className={fieldCls} type="number" min="1" value={form.personas} onChange={(e) => setForm({ ...form, personas: e.target.value })} />
                </Campo>
                <Campo label="Cliente *">
                  <input className={fieldCls} value={form.cliente_nombre} onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })} />
                </Campo>
                <Campo label="Teléfono">
                  <input className={fieldCls} value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
                </Campo>
                <Campo label="Notas">
                  <input className={fieldCls} maxLength={200} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
                </Campo>
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => { setForm(null); setEditId(null); }}>Cancelar</Button>
                <Button size="sm" onClick={() => void guardar()}
                        disabled={!form.mesa_id || !form.cliente_nombre.trim() || !form.hora}>Guardar</Button>
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground border-t pt-2">
            La mesa se pinta MORADA en el Salón con la próxima reserva activa de hoy. Al abrir la cuenta
            marca la reserva como «Llegó» para liberar el aviso.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
